import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Close as CloseIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
  Refresh as RefreshIcon,
  Book as BookIcon
} from '@mui/icons-material';
import { Tooltip, Button } from '@mui/material';
import apiService from '../services/api';

const ExistingContentModal = ({ 
  isOpen, 
  onClose, 
  existingContent, 
  subject, 
  chapter, 
  onSelectContent,
  onGenerateNew 
}) => {
  const [likedStates, setLikedStates] = useState({});
  const [updatingLike, setUpdatingLike] = useState({});

  const handleLike = async (contentId, liked) => {
    if (updatingLike[contentId]) return;
    
    setUpdatingLike(prev => ({ ...prev, [contentId]: true }));
    try {
      await apiService.likeContent(contentId, 'study', liked);
      setLikedStates(prev => ({ ...prev, [contentId]: liked }));
    } catch (error) {
      console.error('Error updating like status:', error);
    } finally {
      setUpdatingLike(prev => ({ ...prev, [contentId]: false }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <BookIcon className="text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">
                Existing Content
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {chapter} • {existingContent.length} versions available
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <CloseIcon className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            {existingContent.map((content, index) => (
              <motion.div
                key={content.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-600 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Version {index + 1}
                      </span>
                      {content.liked && (
                        <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                          Liked
                        </span>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
                        {content.content.title || chapter}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                        {content.content.summary || content.content.detailed_content?.substring(0, 150) + '...'}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {formatDate(content.created_at)}
                      </span>
                      
                      <div className="flex items-center gap-2">
                        <Tooltip title={content.liked ? "Unlike" : "Like"} arrow>
                          <button
                            onClick={() => handleLike(content.id, !content.liked)}
                            disabled={updatingLike[content.id]}
                            className={`p-1.5 rounded-md transition-colors ${
                              content.liked 
                                ? 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30' 
                                : 'text-gray-400 hover:text-green-600 dark:hover:text-green-400'
                            }`}
                          >
                            {content.liked ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                          </button>
                        </Tooltip>
                        
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => onSelectContent(content)}
                          className="text-teal-600 dark:text-teal-400 border-teal-600 dark:border-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20"
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Select an existing version or generate new content
          </p>
          <div className="flex gap-3">
            <Button
              variant="outlined"
              onClick={onClose}
              className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onGenerateNew}
              startIcon={<RefreshIcon />}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              Generate New
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ExistingContentModal; 